import { useState } from "react";
import { Wifi, Copy, Check, Instagram, MessageCircle } from "lucide-react";
import { toast } from "sonner";

function TikTokIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.86a8.1 8.1 0 004.77 1.52V6.9a4.85 4.85 0 01-1-.21z"/>
    </svg>
  );
}

function GoogleIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function SocialConnectSuccess({ data, config, restaurantName }) {
  const [copied, setCopied] = useState(false);

  const coupon = data?.coupon_code || data?.special_offer_coupon;
  const hasSocial = data?.instagram_url || data?.google_review_url || data?.tiktok_url || data?.whatsapp_number;

  const copyPwd = () => {
    if (!data?.wifi_password) return;
    navigator.clipboard.writeText(data.wifi_password).then(() => {
      setCopied(true);
      toast.success("Clave copiada!");
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

      {/* Success header */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: "18px", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
        <div style={{ height: "3px", background: "linear-gradient(90deg,#D32F2F,#B71C1C)" }} />
        <div style={{ padding: "28px 20px 22px", textAlign: "center" }}>
          <div style={{ position: "relative", width: "72px", height: "72px", margin: "0 auto 16px" }}>
            {[0,1].map((i) => (
              <div key={i} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1.5px solid rgba(211,47,47,0.35)", animation: "pulseRing 2s ease-out infinite", animationDelay: `${i * 1}s` }} />
            ))}
            <div style={{ position: "relative", zIndex: 1, width: "72px", height: "72px", borderRadius: "50%", backgroundColor: "#D32F2F", border: "2px solid #111827", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 24px rgba(211,47,47,0.35)" }}>
              <Wifi size={30} style={{ color: "#FFFFFF" }} />
            </div>
          </div>
          <h2 style={{ fontFamily: "Inter,sans-serif", fontSize: "1.5rem", fontWeight: 800, color: "#111827", margin: "0 0 6px", letterSpacing: "-0.01em" }}>
            {data?.is_returning ? "¡Bienvenido de nuevo!" : "¡Acceso activado!"}
          </h2>
          <p style={{ fontSize: "0.85rem", color: "#6B7280", fontFamily: "Inter,sans-serif", margin: 0, lineHeight: 1.5 }}>
            {data?.success_message || config?.success_message || "Copia la clave y conéctate a la red del restaurante."}
          </p>
        </div>
      </div>

      {/* WiFi credentials */}
      {(data?.wifi_ssid || data?.wifi_password) && (
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: "18px", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
          <div style={{ padding: "20px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <p style={{ fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#9CA3AF", textAlign: "center", fontFamily: "Inter,sans-serif", fontWeight: 700, margin: 0 }}>Tu clave Wi-Fi</p>

            {data.wifi_ssid && (
              <div>
                <p style={{ fontSize: "0.6rem", color: "#D1D5DB", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", fontWeight: 700, marginBottom: "3px" }}>Red</p>
                <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827", fontFamily: "Inter,sans-serif", margin: 0 }}>{data.wifi_ssid}</p>
              </div>
            )}

            {data.wifi_password && (
              <>
                <div>
                  <p style={{ fontSize: "0.6rem", color: "#D1D5DB", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", fontWeight: 700, marginBottom: "6px" }}>Contraseña</p>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", padding: "14px", borderRadius: "12px", background: "#F9FAFB", border: "1.5px solid #E5E7EB" }}>
                    <p style={{ flex: 1, margin: 0, fontSize: "1.25rem", fontFamily: "monospace", fontWeight: 700, color: "#111827", wordBreak: "break-all", letterSpacing: "0.04em" }}>{data.wifi_password}</p>
                    <div role="button" tabIndex={0} onClick={copyPwd} onKeyDown={(e) => e.key === "Enter" && copyPwd()}
                      style={{ flexShrink: 0, padding: "10px 12px", borderRadius: "9px", border: `1.5px solid ${copied ? "#16A34A" : "#E5E7EB"}`, backgroundColor: copied ? "rgba(22,163,74,0.08)" : "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", userSelect: "none", transition: "all .2s" }}>
                      {copied ? <Check size={16} style={{ color: "#16A34A" }} /> : <Copy size={16} style={{ color: "#9CA3AF" }} />}
                    </div>
                  </div>
                </div>

                {/* Big copy CTA */}
                <div role="button" tabIndex={0} onClick={copyPwd} onKeyDown={(e) => e.key === "Enter" && copyPwd()}
                  style={{ width: "100%", boxSizing: "border-box", padding: "16px", borderRadius: "12px", backgroundColor: copied ? "#16A34A" : "#111827", border: `2px solid ${copied ? "#15803D" : "#111827"}`, color: "#FFFFFF", fontFamily: "Inter,sans-serif", fontWeight: 800, fontSize: "0.92rem", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer", boxShadow: copied ? "0 6px 24px rgba(22,163,74,0.35)" : "0 6px 24px rgba(0,0,0,0.25)", userSelect: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background-color .3s, box-shadow .3s" }}>
                  {copied ? <Check size={17} /> : <Copy size={17} />}
                  {copied ? "¡Clave copiada!" : "Copiar clave"}
                </div>

                {/* Instructions */}
                <div style={{ background: "#F9FAFB", borderRadius: "10px", padding: "12px 14px", border: "1px solid #F3F4F6" }}>
                  <p style={{ fontSize: "0.78rem", color: "#6B7280", fontFamily: "Inter,sans-serif", margin: 0, lineHeight: 1.55, textAlign: "center" }}>
                    Ve a <strong style={{ color: "#374151" }}>Ajustes → Wi-Fi</strong>, selecciona <strong style={{ color: "#374151" }}>{data.wifi_ssid || "la red del restaurante"}</strong> y pega la clave.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Coupon */}
      {coupon && (
        <div style={{ padding: "18px", borderRadius: "16px", textAlign: "center", background: "#FFFBEB", border: "2px dashed #FCD34D" }}>
          <p style={{ fontSize: "0.58rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#B45309", fontFamily: "Inter,sans-serif", fontWeight: 700, margin: "0 0 4px" }}>Cupón de descuento</p>
          <p style={{ fontSize: "2rem", fontWeight: 800, color: "#92400E", letterSpacing: "0.12em", fontFamily: "Inter,sans-serif", margin: "0 0 4px" }}>{coupon}</p>
          <p style={{ fontSize: "0.72rem", color: "#B45309", fontFamily: "Inter,sans-serif", margin: 0 }}>Muéstrale esta pantalla al utilizarlo</p>
        </div>
      )}

      {/* Social */}
      {hasSocial && (
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "16px 18px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
          <p style={{ fontSize: "0.6rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#9CA3AF", textAlign: "center", fontFamily: "Inter,sans-serif", fontWeight: 700, marginBottom: "10px" }}>Síguenos</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px" }}>
            {data.instagram_url && (
              <a href={data.instagram_url} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px", borderRadius: "10px", background: "#FFFFFF", border: "1.5px solid rgba(225,48,108,0.3)", color: "#DB2777", fontSize: "0.78rem", fontWeight: 600, fontFamily: "Inter,sans-serif", textDecoration: "none" }}>
                <Instagram size={14}/> Instagram
              </a>
            )}
            {data.google_review_url && (
              <a href={data.google_review_url} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px", borderRadius: "10px", background: "#FFFFFF", border: "1.5px solid rgba(66,133,244,0.3)", color: "#374151", fontSize: "0.78rem", fontWeight: 600, fontFamily: "Inter,sans-serif", textDecoration: "none" }}>
                <GoogleIcon size={14}/> Avaliações
              </a>
            )}
            {data.tiktok_url && (
              <a href={data.tiktok_url} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px", borderRadius: "10px", background: "#FFFFFF", border: "1.5px solid #E5E7EB", color: "#111827", fontSize: "0.78rem", fontWeight: 600, fontFamily: "Inter,sans-serif", textDecoration: "none" }}>
                <TikTokIcon size={14}/> TikTok
              </a>
            )}
            {data.whatsapp_number && (
              <a href={`https://wa.me/${data.whatsapp_number.replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px", borderRadius: "10px", background: "#FFFFFF", border: "1.5px solid rgba(37,211,102,0.3)", color: "#16A34A", fontSize: "0.78rem", fontWeight: 600, fontFamily: "Inter,sans-serif", textDecoration: "none" }}>
                <MessageCircle size={14}/> WhatsApp
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
